import React, { forwardRef, useMemo, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';
import { useGetLightning, useMercatorMap } from '@hooks';

import lightningBolt from '../../assets/map/lightning_bolt.png';

const lightningBoltSize = { width: 30, height: 30 };
// Limit the number of bolts to display to something reasonable.
// We also check for duplicate (x, y) positions to help minimize
// the number of bolt images we need to display.
const maxLightningStrikesToDisplay = 1000;

const MapLightningLayerAPI = forwardRef(({ center, zoom, tileDimension, onFinishedDrawing, alpha }, reference) => {
  const strikes = useGetLightning();

  return (
    <MapLightningLayerUI
      alpha={alpha}
      center={center}
      lightningStrikes={strikes}
      ref={reference}
      tileDimension={tileDimension}
      zoom={zoom}
      onFinishedDrawing={onFinishedDrawing}
    />
  );
});

const MapLightningLayerUI = forwardRef(
  ({ center, zoom, tileDimension = 512, onFinishedDrawing, alpha, lightningStrikes }, reference) => {
    // This is the size of the rendered map component.  We use this while calculating map zooms.
    const [mapWidth, setMapWidth] = useState(null);
    const [mapHeight, setMapHeight] = useState(null);

    const mercatorMap = useMercatorMap({
      center,
      zoom,
      tileDimension,
      viewWidth: (mapWidth ?? 1) * Theme.pixelRatio(),
      viewHeight: (mapHeight ?? 1) * Theme.pixelRatio(),
    });

    const bolts = useMemo(() => {
      let displayedBolts = 0;

      const displayedBoltPositions = {};

      const displayingBoltAtLocation = (x, y) => {
        if (displayedBoltPositions[x] == null) {
          displayedBoltPositions[x] = { y: true };
        } else {
          displayedBoltPositions[x][y] = true;
        }
      };

      const boltExistsAtLocation = (x, y) => displayedBoltPositions[x] != null && displayedBoltPositions[x][y] != null;

      const showBoltAtLocation = (x, y) => {
        const boltAlreadyShown = boltExistsAtLocation(x, y);

        const showBolt =
          x > 0 &&
          x <= mapWidth * Theme.pixelRatio() &&
          y > 0 &&
          y <= mapHeight * Theme.pixelRatio() &&
          !boltAlreadyShown;

        if (showBolt) {
          displayingBoltAtLocation(x, y);
          displayedBolts++;
        }

        return showBolt;
      };

      return lightningStrikes
        .map(({ latitude, longitude }) => {
          if (displayedBolts >= maxLightningStrikesToDisplay) {
            return null;
          }

          let [x, y] = mercatorMap.coordsToViewPoint(longitude, latitude);

          x /= Theme.pixelRatio();
          y /= Theme.pixelRatio();

          return (
            showBoltAtLocation(x, y) && (
              <Image
                key={`${latitude}, ${longitude}`}
                source={lightningBolt}
                style={[
                  styles.lightningBoltPosition,
                  {
                    ...lightningBoltSize,
                    left: x - 0.5 * lightningBoltSize.width,
                    top: y - 0.5 * lightningBoltSize.height,
                  },
                ]}
              />
            )
          );
        })
        .filter(Boolean);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lightningStrikes, center, zoom]);

    return (
      <View
        ref={reference}
        style={[{ opacity: alpha }, styles.layerParent]}
        onLayout={event => {
          const { width, height } = event.nativeEvent.layout;

          if (width != null && width !== mapWidth) {
            setMapWidth(width);
          }
          if (height != null && height !== mapHeight) {
            setMapHeight(height);
          }
        }}
      >
        {bolts}
      </View>
    );
  },
);

export const MapLightningLayer = {
  API: MapLightningLayerAPI,
  UI: MapLightningLayerUI,
};

MapLightningLayerAPI.displayName = 'MapLightningLayerAPI';

MapLightningLayerAPI.propTypes = {
  center: PropTypes.arrayOf(PropTypes.number).isRequired,
  zoom: PropTypes.number.isRequired,
  // Called when the layer is fully rendered on the map.
  onFinishedDrawing: PropTypes.func,
  // We aren't tiling images, but this is used to convert between
  // lat/lon and view coordinates.  This should match the dimension
  // of the weather tiles fetched from api.weather.com.
  tileDimension: PropTypes.number,
  alpha: PropTypes.number,
};

MapLightningLayerAPI.defaultProps = {
  tileDimension: 512,
  onFinishedDrawing: () => {},
  alpha: 1,
};

const styles = StyleSheet.create({
  layerParent: {
    bottom: Theme.px(0),
    left: Theme.px(0),
    overflow: 'hidden',
    position: 'absolute',
    right: Theme.px(0),
    top: Theme.px(0),
  },
  lightningBoltPosition: {
    position: 'absolute',
  },
});
